/** Formatting helpers. Missing values render as words, never as zero. */

let displayTimeZone: string | undefined;

/** Personal display timezone from the account; tenant timezone is shown explicitly where it matters. */
export function setDisplayTimeZone(tz: string | undefined): void {
  displayTimeZone = tz || undefined;
}

function toDate(value: string | number | Date | null | undefined): Date | null {
  if (value === null || value === undefined || value === '') return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function formatDateTime(value: string | number | Date | null | undefined, timeZone?: string): string {
  const d = toDate(value);
  if (!d) return 'Not set';
  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
    timeZone: timeZone ?? displayTimeZone,
    timeZoneName: timeZone ? 'short' : undefined,
  }).format(d);
}

export function formatDate(value: string | number | Date | null | undefined, timeZone?: string): string {
  const d = toDate(value);
  if (!d) return 'Not set';
  return new Intl.DateTimeFormat(undefined, { year: 'numeric', month: 'short', day: 'numeric', timeZone: timeZone ?? displayTimeZone }).format(d);
}

const UNITS: Array<[Intl.RelativeTimeFormatUnit, number]> = [
  ['year', 365 * 24 * 3600],
  ['month', 30 * 24 * 3600],
  ['week', 7 * 24 * 3600],
  ['day', 24 * 3600],
  ['hour', 3600],
  ['minute', 60],
  ['second', 1],
];

export function formatRelative(value: string | number | Date | null | undefined, now: number = Date.now()): string {
  const d = toDate(value);
  if (!d) return 'Never';
  const diff = Math.round((d.getTime() - now) / 1000);
  const abs = Math.abs(diff);
  if (abs < 45) return diff >= 0 ? 'in a few seconds' : 'just now';
  const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });
  for (const [unit, secs] of UNITS) {
    if (abs >= secs || unit === 'second') return rtf.format(Math.round(diff / secs), unit);
  }
  return '';
}

/** "1 h 04 min", "58 min", "45 s": remaining time for a positive number of seconds. */
export function formatRemaining(totalSeconds: number): string {
  const s = Math.max(0, Math.ceil(totalSeconds));
  if (s < 60) return `${s} s`;
  const m = Math.ceil(s / 60);
  if (m < 60) return `${m} min`;
  return `${Math.floor(m / 60)} h ${String(m % 60).padStart(2, '0')} min`;
}

/** "15:42": the one clock format in the app, always 24 hour, in the display time zone. */
export function formatTime(value: string | number | Date, timeZone?: string): string {
  return new Intl.DateTimeFormat(undefined, { hour: '2-digit', minute: '2-digit', hourCycle: 'h23', timeZone: timeZone ?? displayTimeZone }).format(new Date(value));
}

/** "15:42" today, "Tue 15:42" otherwise. */
export function formatClock(value: string | number | Date | null | undefined, timeZone?: string): string {
  const d = toDate(value);
  if (!d) return 'Not set';
  const tz = timeZone ?? displayTimeZone;
  const day = (x: Date) => new Intl.DateTimeFormat('en-CA', { timeZone: tz }).format(x);
  const time = formatTime(d, tz);
  if (day(d) === day(new Date())) return time;
  return `${new Intl.DateTimeFormat(undefined, { weekday: 'short', timeZone: tz }).format(d)} ${time}`;
}

export function formatSeconds(totalSeconds: number | null | undefined): string {
  if (totalSeconds === null || totalSeconds === undefined) return 'Not set';
  if (totalSeconds % 3600 === 0) return `${totalSeconds / 3600} h`;
  if (totalSeconds % 60 === 0) return `${totalSeconds / 60} min`;
  return `${totalSeconds} s`;
}

export function formatNumber(value: number | string | null | undefined, unit?: string): string {
  if (value === null || value === undefined || value === '') return 'Unknown';
  const n = typeof value === 'string' ? Number(value) : value;
  if (!Number.isFinite(n)) return String(value);
  const text = new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(n);
  return unit ? `${text} ${unit}` : text;
}

/** Power stored canonically in kW (decimal string). Shows MW above 1000 kW. */
export function formatPowerKw(value: number | string | null | undefined): string {
  if (value === null || value === undefined || value === '') return 'Unknown';
  const n = typeof value === 'string' ? Number(value) : value;
  if (!Number.isFinite(n)) return String(value);
  return n >= 1000 ? formatNumber(n / 1000, 'MW') : formatNumber(n, 'kW');
}

export function shortId(id: string | null | undefined): string {
  if (!id) return '';
  return id.length > 12 ? `${id.slice(0, 4)}…${id.slice(-6)}` : id;
}

export function humanize(value: string | null | undefined): string {
  if (!value) return 'Unknown';
  const s = value.replace(/[_.]+/g, ' ').trim();
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export function initials(name: string | null | undefined): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p.charAt(0).toUpperCase()).join('') || '?';
}
